import * as v from "valibot";
import { rpcContract } from "../rpc";

/**
 * Custom validation for redirect URLs to prevent open redirect attacks.
 * Allows relative URLs and URLs from trusted domains only.
 */
const safeRedirectUrlSchema = v.pipe(
  v.string(),
  v.check((url) => {
    // Block protocol-relative URLs
    if (url.startsWith("//")) return false;

    // Allow relative URLs
    if (url.startsWith("/")) return true;

    try {
      const parsed = new URL(url);
      // Block non-http(s) protocols
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return false;

      // Allow only trusted domains (production, staging, and local development)
      const allowedHosts = [
        "worldofwow.com",
        "www.worldofwow.com",
        "worldofwow.dev",
        "www.worldofwow.dev",
        "localhost",
        "127.0.0.1",
      ];
      const hostname = parsed.hostname.toLowerCase();
      return allowedHosts.some((h) => hostname === h || hostname.endsWith(`.${h}`));
    } catch {
      return false;
    }
  }, "Invalid redirect URL"),
);

// Redirect metadata for checkout flows during OAuth authentication
export const oauthRedirectMetadataSchema = v.object({
  returnUrl: v.optional(safeRedirectUrlSchema),
  autoCheckout: v.optional(v.picklist(["live-event", "post", "link"])),
  eventId: v.optional(v.string()),
  contentId: v.optional(v.string()),
});

export type OAuthRedirectMetadata = v.InferOutput<typeof oauthRedirectMetadataSchema>;

// Social Auth Init - Get OAuth URL
export const socialAuthInitResultSchema = v.object({
  authUrl: v.string(),
  state: v.string(),
});

export type SocialAuthInitResult = v.InferOutput<typeof socialAuthInitResultSchema>;

export const socialAuthInitRpcContract = rpcContract({
  name: "socialAuthInit",
  paramsSchema: v.object({
    provider: v.pipe(v.string(), v.picklist(["google", "facebook", "apple"])),
    redirectMetadata: v.optional(oauthRedirectMetadataSchema),
  }),
  resultSchema: socialAuthInitResultSchema,
});

export type SocialAuthInitParams = v.InferOutput<typeof socialAuthInitRpcContract.paramsSchema>;

// Social Auth Callback - Handle OAuth callback
export const socialAuthCallbackResultSchema = v.object({
  accessToken: v.string(),
  isNewUser: v.boolean(),
  user: v.object({
    id: v.string(),
    email: v.string(),
    name: v.string(),
    handle: v.optional(v.string()),
  }),
});

export type SocialAuthCallbackResult = v.InferOutput<typeof socialAuthCallbackResultSchema>;

export const socialAuthCallbackRpcContract = rpcContract({
  name: "socialAuthCallback",
  paramsSchema: v.object({
    provider: v.pipe(v.string(), v.picklist(["google", "facebook", "apple"])),
    code: v.optional(v.string()), // For OAuth code flow
    token: v.optional(v.string()), // For token-based flow (frontend SDKs)
    state: v.string(),
    redirectUri: v.string(),
    // Apple-specific fields
    user: v.optional(v.string()), // Apple sends user info separately
  }),
  resultSchema: socialAuthCallbackResultSchema,
});

export type SocialAuthCallbackParams = v.InferOutput<typeof socialAuthCallbackRpcContract.paramsSchema>;

// Social Auth Link - Link social account to existing user
export const socialAuthLinkRpcContract = rpcContract({
  name: "socialAuthLink",
  paramsSchema: v.object({
    provider: v.pipe(v.string(), v.picklist(["google", "facebook", "apple"])),
    token: v.string(),
  }),
  resultSchema: v.object({
    success: v.boolean(),
  }),
});

export type SocialAuthLinkParams = v.InferOutput<typeof socialAuthLinkRpcContract.paramsSchema>;
export type SocialAuthLinkResult = v.InferOutput<typeof socialAuthLinkRpcContract.resultSchema>;

// Social Auth Unlink - Unlink social account from user
export const socialAuthUnlinkRpcContract = rpcContract({
  name: "socialAuthUnlink",
  paramsSchema: v.object({
    provider: v.pipe(v.string(), v.picklist(["google", "facebook", "apple"])),
  }),
  resultSchema: v.object({
    success: v.boolean(),
  }),
});

export type SocialAuthUnlinkParams = v.InferOutput<typeof socialAuthUnlinkRpcContract.paramsSchema>;
export type SocialAuthUnlinkResult = v.InferOutput<typeof socialAuthUnlinkRpcContract.resultSchema>;

// Get Linked Providers - Get list of linked social providers for user
export const getLinkedProvidersResultSchema = v.object({
  providers: v.array(
    v.object({
      provider: v.string(),
      email: v.string(),
      linkedAt: v.string(), // ISO date string
    }),
  ),
});

export type GetLinkedProvidersResult = v.InferOutput<typeof getLinkedProvidersResultSchema>;

export const getLinkedProvidersRpcContract = rpcContract({
  name: "getLinkedProviders",
  paramsSchema: v.object({}),
  resultSchema: getLinkedProvidersResultSchema,
});

export type GetLinkedProvidersParams = v.InferOutput<typeof getLinkedProvidersRpcContract.paramsSchema>;
